import { privateHttp } from "../../config/axios";
import { API_ENDPOINTS } from "../../config/endpoints";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import {
  UserReserveRequest,
  UserReserveResponse,
  UserReserveUpdateRequest,
  UserReserveUpdateResponse,
  UserReserveCancelResponse,
  PetDataType,
} from "../../types/common";

interface CreateReservationRequest {
  storeId: string;
  serviceType: "DAYCARE" | "HOTEL" | "BEAUTY";
  startDate: string;
  endDate: string;
  petId: string;
  additionalServices?: string[];
  memo?: string;
}

interface ReservationResponse {
  id: string;
  status: "PENDING" | "CONFIRMED" | "CANCELLED";
  store: {
    id: string;
    name: string;
  };
  service: {
    type: string;
    price: number;
  };
  period: {
    start: string;
    end: string;
  };
  pet: {
    id: string;
    name: string;
    breed: string;
  };
  totalAmount: number;
  createdAt: string;
}

interface ReservationListRequest {
  page: number;
  limit: number;
  status?: "PENDING" | "CONFIRMED" | "CANCELLED";
  startDate?: string;
  endDate?: string;
}

interface ReservationListResponse {
  totalCount: number;
  items: ReservationResponse[];
}

// 예약 생성
export const createReservation = async (
  request: CreateReservationRequest
): Promise<ReservationResponse> => {
  const { data } = await privateHttp.post<ReservationResponse>(
    API_ENDPOINTS.RESERVATION.CREATE,
    request
  );
  return data;
};

// 예약 정원 체크 (유치원 정원 초과 여부). 예약 진행 전 호출.
export interface ReservationCapacityResult {
  limitEnabled: boolean;
  capacity: number | null;
  dates: { date: string; count: number; full: boolean }[];
  fullDates: string[];
}
export const checkReservationCapacity = async (
  companySeq: number,
  dates: string[]
): Promise<ReservationCapacityResult> => {
  const { data } = await privateHttp.get<{ data: ReservationCapacityResult }>(
    API_ENDPOINTS.RESERVATION.CAPACITY_CHECK,
    { params: { companySeq, dates: dates.join(",") } }
  );
  return (
    data?.data ?? { limitEnabled: false, capacity: null, dates: [], fullDates: [] }
  );
};

// 예약 목록 조회
export const getReservationList = async (
  request: ReservationListRequest
): Promise<ReservationListResponse> => {
  const { data } = await privateHttp.get<ReservationListResponse>(
    API_ENDPOINTS.RESERVATION.LIST,
    { params: request }
  );
  return data;
};

// 예약 상세 조회
export const getReservationDetail = async (
  reservationId: string
): Promise<ReservationResponse> => {
  const { data } = await privateHttp.get<ReservationResponse>(
    `${API_ENDPOINTS.RESERVATION.DETAIL}/${reservationId}`
  );
  return data;
};

// 예약 취소
export const cancelReservation = async (
  reservationId: string
): Promise<ReservationResponse> => {
  const { data } = await privateHttp.post<ReservationResponse>(
    `${API_ENDPOINTS.RESERVATION.CANCEL}/${reservationId}`
  );
  return data;
};

// 상품 목록 조회 요청 타입
interface ProductListRequest {
  companySeq: number;
}

// 상품 목록 응답 타입
interface ProductListResponse {
  statusCode: boolean;
  message: string;
  data?: Array<{
    cdProvideType: string;
    productName: string;
    active: boolean;
    select: boolean;
  }>;
}

// 상품 목록 조회
export const getProductList = async (
  request: ProductListRequest
): Promise<ProductListResponse> => {
  try {
    const { data } = await privateHttp.get<ProductListResponse>(
      API_ENDPOINTS.RESERVATION.PRODUCT_LIST,
      { params: request }
    );
    return data;
  } catch (error) {
    console.error("상품 목록 조회 에러:", error);
    throw error;
  }
};

// 상품 목록 조회 useQuery 훅
export const useProductList = (companySeq: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["product-list", companySeq],
    queryFn: () => getProductList({ companySeq }),
    enabled: enabled && !!companySeq,
    select: (data) => data.data || [],
  });
};

// 예약 폼 조회 요청 타입
interface ReserveFormsRequest {
  companySeq: number;
  petSeq: number;
  cdProductType: string;
}

// 예약 폼 응답 타입(예시, 실제 응답 구조에 맞게 수정)
interface ReserveFormsResponse {
  statusCode: number;
  message: string;
  data?: {
    gradeConfirm: boolean;
    reserveForm: {
      endMinute?: string;
      endHour?: string;
      startMinute?: string;
      startHour?: string;
      // 데이케어 시간권: 이용시간 Select 구성용 (null이면 제한 없음/보유 이용권 없음)
      minUseHours?: number | null;
      maxUseHours?: number | null;
      paymentCoin: number;
      productSeq: number;
      dayOfWeeks?: Array<{
        cdDayOfWeek: string;
        dayOfWeekName: string;
        active?: boolean;
        select?: boolean;
      }>;
      additionals: Array<{
        productSeq?: number;
        additionalCompanySeq?: number;
        additionalName: string;
        paymentCoin: number;
        active: boolean;
        select: boolean;
      }>;
    };
  };
}

// 예약 폼 조회
export const getReserveForms = async (
  request: ReserveFormsRequest
): Promise<ReserveFormsResponse> => {
  try {
    const { data } = await privateHttp.get<ReserveFormsResponse>(
      API_ENDPOINTS.RESERVATION.FORMS,
      { params: request }
    );
    return data;
  } catch (error) {
    console.error("예약 폼 조회 에러:", error);
    throw error;
  }
};

// 예약 폼 useQuery 훅
export const useReserveForms = (
  companySeq: number,
  petSeq: number,
  cdProductType: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["reserve-forms", companySeq, petSeq, cdProductType],
    queryFn: () => getReserveForms({ companySeq, petSeq, cdProductType }),
    enabled: enabled && !!companySeq && !!petSeq && !!cdProductType,
    select: (data) => data.data || null,
  });
};

// 코인 계산 요청 타입
interface CalculateCoinRequest {
  reservationSeq: number | null;
  cdProductType: string;
  gradeConfirm: boolean;
  paymentCoinTotal: number | null;
  companySeq: number;
  reserveForm: {
    productSeq: number;
    dtReservStart: string | null;
    dtReservEnd?: string | null;
    dayOfWeeks?: Array<{
      cdDayOfWeek: string;
      dayOfWeekName: string;
      select: boolean;
    }>;
    paymentCoin: number;
    additionals: Array<{
      productSeq?: number;
      additionalCompanySeq?: number;
      paymentCoin: number;
      additionalName: string;
      active: boolean;
      select: boolean;
    }>;
    pets: Array<{
      petSeq: number;
      petName: string;
      select: boolean;
    }>;
    memo: string | null;
  };
}

// 코인 계산 응답 타입
interface CalculateCoinResponse {
  statusCode: number;
  message: string;
  data?: {
    paymentCoinTotal: number;
    coinDiff: number;
  };
}

// 코인 계산 API
export const calculateCoin = async (
  request: CalculateCoinRequest
): Promise<CalculateCoinResponse> => {
  try {
    const { data } = await privateHttp.post<CalculateCoinResponse>(
      API_ENDPOINTS.RESERVATION.CALCULATE_COIN,
      request
    );
    return data;
  } catch (error) {
    console.error("코인 계산 에러:", error);
    throw error;
  }
};

// 코인 계산 useMutation 훅
export const useCalculateCoin = () => {
  return useMutation({
    mutationFn: calculateCoin,
  });
};

// 그루밍 시간 체크 요청 타입
interface GroomingTimeCheckRequest {
  companySeq: number;
  date: string;
}

// 그루밍 시간 체크 응답 타입
interface GroomingTimeCheckResponse {
  statusCode: number;
  message: string;
  data?: Array<{
    timeName: string;
    active: boolean;
    select: boolean;
  }>;
}

// 그루밍 시간 체크 API
export const getGroomingTimeCheck = async (
  request: GroomingTimeCheckRequest
): Promise<GroomingTimeCheckResponse> => {
  try {
    const { data } = await privateHttp.get<GroomingTimeCheckResponse>(
      API_ENDPOINTS.RESERVATION.GROOMING_TIME_CHECK,
      { params: request }
    );
    return data;
  } catch (error) {
    console.error("그루밍 시간 체크 에러:", error);
    throw error;
  }
};

// 그루밍 시간 체크 useQuery 훅
export const useGroomingTimeCheck = (
  companySeq: number,
  date: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["grooming-time-check", companySeq, date],
    queryFn: () => getGroomingTimeCheck({ companySeq, date }),
    enabled: enabled && !!companySeq && !!date,
    select: (data) => data.data || null,
  });
};

// 예약 생성 요청 타입
interface ReserveRequest {
  reservationSeq: number | null;
  cdProductType: string;
  gradeConfirm: boolean;
  paymentCoinTotal: number | null;
  companySeq: number;
  forceProceed?: boolean;
  tickets: Array<{
    purchaseTicketSeq: number;
    productTicketSeq: number;
    paymentTicket: number;
  }>;
  reserveForm: {
    productSeq?: number;
    dates?: string[]; // 선택한 날짜들을 yyyy-mm-dd 형식으로 배열로 전송
    dtReservStart?: string;
    dtReservEnd?: string;
    startTime?: string;
    endTime?: string;
    dayOfWeeks?: Array<{
      cdDayOfWeek: string;
      dayOfWeekName: string;
      select: boolean;
    }>;
    paymentCoin?: number;
    additionals: Array<{
      productSeq?: number;
      additionalCompanySeq?: number;
      paymentCoin: number;
      additionalName: string;
      active: boolean;
      select: boolean;
    }>;
    pets: Array<{
      petSeq: number;
      petName: string;
      select: boolean;
    }>;
    memo: string | null;
  };
}

// 중복 예약 응답 코드
export const RESERVE_CONFLICT_STATUS = {
  ALREADY_RESERVED_SAME_COMPANY: 4009,
  ALREADY_RESERVED_OTHER_COMPANY: 4010,
} as const;

export interface ReserveConflictData {
  responseCode:
    | "ALREADY_RESERVED_SAME_COMPANY"
    | "ALREADY_RESERVED_OTHER_COMPANY";
  conflictCompanySeq: number;
  conflictCompanyName: string;
}

// 예약 생성 응답 타입
interface ReserveResponse {
  statusCode: number;
  message: string;
  data?: {
    hasContract?: boolean;
    reservationSeq?: number;
    responseCode?: string;
    conflictCompanySeq?: number;
    conflictCompanyName?: string;
  };
}

// 예약 생성 API
export const createReserve = async (
  request: ReserveRequest
): Promise<ReserveResponse> => {
  try {
    const { data } = await privateHttp.post<ReserveResponse>(
      API_ENDPOINTS.RESERVATION.RESERVE,
      request
    );
    return data;
  } catch (error) {
    console.error("예약 생성 에러:", error);
    throw error;
  }
};

// 예약 생성 useMutation 훅
export const useCreateReserve = () => {
  return useMutation({
    mutationFn: createReserve,
  });
};

// 코인 잔액 확인 API
interface HasBalanceCoinRequest {
  totalCoin: number;
}

interface HasBalanceCoinResponse {
  statusCode: number;
  data: {
    ticketPurchasable: boolean;
    coinPurchasable: boolean;
    props: {
      type: string;
      datas: {
        balanceCoin: number;
        totalCoin: number;
      }[];
    }[];
  };
  message: string;
}

const getHasBalanceCoin = async (
  requestData: HasBalanceCoinRequest
): Promise<HasBalanceCoinResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.RESERVATION.HAS_BALANCE_COIN,
      {
        params: requestData,
      }
    );
    return data;
  } catch (error) {
    console.error("코인 잔액 확인 실패:", error);
    throw error;
  }
};

export const useHasBalanceCoin = (totalCoin: number) => {
  return useQuery({
    queryKey: ["reservation", "has-balance-coin", totalCoin],
    queryFn: () => getHasBalanceCoin({ totalCoin }),
    enabled: !!totalCoin && totalCoin > 0,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  });
};

// 임시 예약 업데이트 요청 타입
interface UpdateTempRequest {
  reservationSeq: number;
  contractCancel: boolean;
}

// 임시 예약 업데이트 응답 타입
interface UpdateTempResponse {
  statusCode: number;
  message: string;
  data?: {
    reservationSeq: number;
    contractCancel: boolean;
  };
}

// 임시 예약 업데이트 API
export const updateTempReserve = async (
  request: UpdateTempRequest
): Promise<UpdateTempResponse> => {
  try {
    const { data } = await privateHttp.patch<UpdateTempResponse>(
      API_ENDPOINTS.RESERVATION.UPDATE_TEMP,
      request
    );
    return data;
  } catch (error) {
    console.error("임시 예약 업데이트 에러:", error);
    throw error;
  }
};

// 임시 예약 업데이트 useMutation 훅
export const useUpdateTempReserve = () => {
  return useMutation({
    mutationFn: updateTempReserve,
  });
};

// 계약 생성 요청 타입
interface CreateContractRequest {
  data: {
    companySeq: number;
    userSeq: number;
    petSeq: number;
    contractSeq: number;
    userName: string;
    phoneNumber: string;
    addr: string | null;
    addrDetail: string | null;
    signatureFileSeq: number | null;
    fileUrl: string | null;
    petName: string;
    privacyYn: string | null;
    contractName: string;
    contractUrl: string | null;
    contractContents: string;
    contractDocJson?: string | null;
    fieldValues?: Record<string, string>;
    companyInfo: {
      companyName: string | null;
      businessNumber: string | null;
    };
    userInfo: {
      userName: string | null;
      nickname: string | null;
      email: string | null;
      phoneNumber: string | null;
      address: string | null;
    };
    petInfo: {
      petName: string;
      petBirthday: string;
      petBreed: string;
      petWeight: string;
      neuter: string;
      inoculation: string;
      personality: string;
      allergy: string;
      memo: string;
    };
  };
  signature: File | string; // 서명 파일 또는 Base64 문자열
}

// 계약 생성 응답 타입
interface CreateContractResponse {
  statusCode: number;
  message: string;
  data?: {
    contractSeq: number;
    status: string;
  };
}

// 계약 생성 API
export const createContract = async (
  request: CreateContractRequest
): Promise<CreateContractResponse> => {
  try {
    // FormData 생성
    const formData = new FormData();
    console.log(request);
    // data 객체를 JSON 문자열로 변환하여 추가
    formData.append("param", JSON.stringify(request.data));

    // signature 파일 처리
    if (request.signature) {
      if (request.signature instanceof File) {
        formData.append("file", request.signature);
      } else if (
        typeof request.signature === "string" &&
        request.signature.startsWith("data:")
      ) {
        // Base64 문자열을 Blob으로 변환
        const response = await fetch(request.signature);
        const blob = await response.blob();
        formData.append("file", blob, "signature.png");
      }
    }

    const { data } = await privateHttp.post<CreateContractResponse>(
      API_ENDPOINTS.RESERVATION.CONTRACT,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  } catch (error) {
    console.error("계약 생성 에러:", error);
    throw error;
  }
};

// 계약 생성 useMutation 훅
export const useCreateContract = () => {
  return useMutation({
    mutationFn: createContract,
  });
};

// 계약 조회 요청 타입
interface GetContractRequest {
  companySeq: number;
  petSeq: number;
}

// 계약 조회 응답 타입
interface GetContractResponse {
  statusCode: 1073741824;
  data: {
    companySeq: number;
    userSeq: number;
    petSeq: number;
    contractSeq: number;
    userName: string | null;
    phoneNumber: string | null;
    addr: string | null;
    addrDetail: string | null;
    signatureFileSeq: number;
    fileUrl: string | null;
    petName: string;
    privacyYn: string;
    contractName: string;
    contractUrl: string;
    contractContents: string;
    contractDocJson?: string | null;
    renderedVersionSeq?: number | null;
    renderedPdfSeq?: number | null;
    renderedPdfUrl?: string | null;
    renderedPageFileSeqs?: string | null;
    renderedPageFileUrls?: string[] | null;
    placementManifest?: string | null;
    /** 사장님 활성 기본 도장 이미지 URL — seal 필드 자리에 표시 */
    sealImageUrl?: string | null;
    /** [varName] 마커 prefilled 값 (guardianName, petName, companyName, businessNumber, contractDate ...) */
    variables?: Record<string, string> | null;
    companyInfo: {
      companyName: string | null;
      businessNumber: string | null;
    };
    userInfo: {
      userName: string | null;
      nickname: string | null;
      email: string | null;
      phoneNumber: string | null;
      address: string | null;
    };
    petInfo: {
      petName: string;
      petBirthday: string;
      petBreed: string;
      petWeight: string;
      neuter: string;
      inoculation: string;
      personality: string;
      allergy: string;
      memo: string;
    };
  };
  message: string;
}

// 계약 조회 API
export const getContract = async (
  request: GetContractRequest
): Promise<GetContractResponse> => {
  const { data } = await privateHttp.get<GetContractResponse>(
    API_ENDPOINTS.RESERVATION.GET_CONTRACT,
    { params: request }
  );
  return data;
};

// 계약 조회 훅
export const useGetContract = (
  companySeq: number,
  petSeq: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["contract", companySeq, petSeq],
    queryFn: () => getContract({ companySeq, petSeq }),
    enabled: enabled && !!companySeq && !!petSeq,
  });
};

// User reserve 조회 API
const getUserReserve = async (
  params: UserReserveRequest
): Promise<UserReserveResponse> => {
  try {
    const { data } = await privateHttp.get(API_ENDPOINTS.RESERVATION.RESERVE, {
      params: {
        reservationSeq: params.reservationSeq,
      },
    });
    return data;
  } catch (error) {
    console.error("User reserve API 호출 에러:", error);
    throw error;
  }
};

// User reserve 조회 훅
export const useUserReserve = (params: UserReserveRequest) => {
  return useQuery({
    queryKey: ["user", "reserve", params.reservationSeq],
    queryFn: () => getUserReserve(params),
    enabled: !!params.reservationSeq,
    staleTime: 0, // 즉시 stale로 설정
    gcTime: 0, // 캐시하지 않음
  });
};

// User reserve 업데이트 API
const updateUserReserve = async (
  params: UserReserveUpdateRequest
): Promise<UserReserveUpdateResponse> => {
  try {
    const { data } = await privateHttp.patch(
      API_ENDPOINTS.RESERVATION.UPDATE,
      params
    );
    return data;
  } catch (error) {
    console.error("User reserve 업데이트 API 호출 에러:", error);
    throw error;
  }
};

// User reserve 업데이트 훅
export const useUpdateUserReserve = () => {
  return useMutation({
    mutationFn: updateUserReserve,
    onSuccess: (data) => {
      console.log("예약 업데이트 성공:", data);
    },
    onError: (error) => {
      console.error("예약 업데이트 실패:", error);
    },
  });
};

// User reserve 취소 API
const cancelUserReserve = async (
  params: any
): Promise<UserReserveCancelResponse> => {
  try {
    const { data } = await privateHttp.patch(
      API_ENDPOINTS.RESERVATION.CANCEL_USER,
      params
    );
    return data;
  } catch (error) {
    console.error("User reserve 취소 API 호출 에러:", error);
    throw error;
  }
};

// User reserve 취소 훅
export const useCancelUserReserve = () => {
  return useMutation({
    mutationFn: cancelUserReserve,
    onSuccess: (data) => {
      console.log("예약 취소 성공:", data);
    },
    onError: (error) => {
      console.error("예약 취소 실패:", error);
    },
  });
};

// 예약 가능 날짜 조회 요청 타입
interface ReservableDateRequest {
  companySeq: number;
  yearMonth: string; // YYYY-MM 형식
  productType?: string;
  petSeq?: number;
}

// 예약 가능 날짜 응답 타입
interface ReservableDateResponse {
  statusCode: number;
  data?: {
    calendar: any;
    reservedDates?: string[]; // 같은 매장·같은 펫·같은 그룹으로 이미 예약된 날짜 (yyyyMMdd)
    holidays?: string[]; // 매장 임시 휴무일 (yyyyMMdd) — 호텔은 calendar에 미반영(체크인만 차단 정책)
    deadlineDates?: string[]; // 예약 마감시간 설정으로 차단된 날짜 (yyyyMMdd, 당일 + 마감 이후 익일)
  };
}

// 예약 가능 날짜 조회 API
export const getReservableDate = async (
  request: ReservableDateRequest
): Promise<ReservableDateResponse> => {
  try {
    const { data } = await privateHttp.get<ReservableDateResponse>(
      API_ENDPOINTS.RESERVATION.RESERVABLE_DATE,
      { params: request }
    );
    return data;
  } catch (error) {
    console.error("예약 가능 날짜 조회 에러:", error);
    throw error;
  }
};

// 예약 가능 날짜 조회 useQuery 훅
export const useReservableDate = (
  companySeq: number,
  yearMonth: string,
  enabled: boolean = true,
  productType?: string,
  petSeq?: number
) => {
  return useQuery({
    queryKey: ["reservable-date", companySeq, yearMonth, productType, petSeq],
    queryFn: () =>
      getReservableDate({ companySeq, yearMonth, productType, petSeq }),
    enabled: enabled && !!companySeq && !!yearMonth,
    select: (data) => data.data || null,
  });
};

// 예약 가능 시간 조회 요청 타입
interface ReservableTimeRequest {
  companySeq: number;
  yearMonthDay: string;
}

// 예약 가능 시간 응답 타입
interface ReservableTimeResponse {
  statusCode: number;
  data?: {
    calendar: any;
    startTime: string;
    endTime: string;
  };
}

// 예약 가능 시간 조회 API
export const getReservableTime = async (
  request: ReservableTimeRequest
): Promise<ReservableTimeResponse> => {
  try {
    const { data } = await privateHttp.get<ReservableTimeResponse>(
      API_ENDPOINTS.RESERVATION.RESERVABLE_TIME,
      { params: request }
    );
    return data;
  } catch (error) {
    console.error("예약 가능 시간 조회 에러:", error);
    throw error;
  }
};

// 예약 가능 시간 조회 useQuery 훅
export const useReservableTime = (
  companySeq: number,
  yearMonthDay: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["reservable-time", companySeq, yearMonthDay],
    queryFn: () => getReservableTime({ companySeq, yearMonthDay }),
    enabled: enabled && !!companySeq && !!yearMonthDay,
    select: (data) => data.data || null,
    placeholderData: keepPreviousData,
  });
};

// 예약용 반려견 목록 조회 요청 타입
interface ReservationPetListRequest {
  companySeq: number;
}

// 예약용 반려견 목록 조회 응답 타입
interface ReservationPetListResponse {
  statusCode: number;
  data: PetDataType[];
  message: string;
}

// 예약용 반려견 목록 조회 API
export const getReservationPetList = async (
  request: ReservationPetListRequest
): Promise<ReservationPetListResponse> => {
  try {
    const { data } = await privateHttp.get<ReservationPetListResponse>(
      API_ENDPOINTS.RESERVATION.PET_LIST,
      { params: request }
    );
    return data;
  } catch (error) {
    console.error("예약용 반려견 목록 조회 에러:", error);
    throw error;
  }
};

// 예약용 반려견 목록 조회 useQuery 훅
export const useReservationPetList = (
  companySeq: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["reservation-pet-list", companySeq],
    queryFn: () => getReservationPetList({ companySeq }),
    enabled: enabled && !!companySeq,
    select: (data) => data.data || [],
  });
};
